import { CustomDatePicker } from '@components/controls/CustomDatePicker';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useGetMacroplanogramResults from '@store/hooks/useGetMacroplanogramResults';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Alert from '@amzn/meridian/alert';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import StoreSelector from 'src/components/selectors/ProgramStoreSelector';
import NavigationHelper from '@components/agile/utils/URLParametersUtils';

export interface AtAgileHeaderProps {
  setZoneDetailsAndTime: (
    zoneDetails: AgileMacroplanogram | undefined,
    swapTime: number,
    macroplanogramList: AgileMacroplanogram[] | undefined,
  ) => void;
  urlParams: AgileURLParameterTypes;
  setUrlParameter: (urlParams: AgileURLParameterTypes) => void;
  setRecommendationFlag?: (RecommendationFlag: boolean) => void;
  enableZoneListInHeader: boolean;
  includeDraft: boolean;
  setIncludeDraft: (value: boolean) => void;
}

const AtAgileHeader = (props: AtAgileHeaderProps) => {
  const {
    setZoneDetailsAndTime,
    urlParams,
    setUrlParameter,
    setRecommendationFlag,
    enableZoneListInHeader,
    includeDraft,
    setIncludeDraft,
  } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const [store, setStore] = useState(urlParams?.storeId);
  const [date, setDate] = useState(urlParams?.date);
  const [zoneId, setZoneId] = useState(urlParams?.zoneId);
  const [zoneSelectionFlag, setZoneSelectionFlag] = useState(false);
  const timeStamp = new Date(date).getTime();
  const { macroplanogramList, zoneNameList, isSuccess } = useGetMacroplanogramResults({
    storeId: store,
    timestamp: timeStamp,
    includeFuture: false,
    includeDraft: true,
  });
  const zoneSelectionValidation = (zone: AgileMacroplanogram | undefined) => {
    if (zone === undefined || zone === null) {
      setZoneSelectionFlag(true);
    } else {
      setZoneSelectionFlag(false);
    }
  };
  const filteredZoneList = zoneNameList?.filter((zone) => zone.isDraft === includeDraft) || [];

  const handleZoneChange = (newZoneId: string) => {
    setZoneId(newZoneId);
    setZoneSelectionFlag(newZoneId === ''); // Show error only when "None" is selected
  };
  // TO-DO: remove this once all the draft changes are tested and deployed
  const draftChanges = true;

  useEffect(() => {
    setSearchParams(
      NavigationHelper.createSearchParams(
        {
          storeId: store,
          date,
          zoneId,
        },
        urlParams.demo,
      ),
    );
    setUrlParameter({
      storeId: store,
      date,
      zoneId,
      demo: urlParams.demo,
    });
    console.log('searchParams:', searchParams);
    if (zoneId !== undefined && isSuccess) {
      // Filter macroplanogramList based on includeDraft
      const filteredMacroplanogramList = macroplanogramList?.filter((obj) => obj.isDraft === includeDraft) ?? [];
      const zoneDetails: AgileMacroplanogram | undefined = filteredMacroplanogramList?.find(
        (obj) => obj.entryId === zoneId,
      );
      setZoneDetailsAndTime(zoneDetails, timeStamp, filteredMacroplanogramList ?? []);
      zoneSelectionValidation(zoneDetails);
    }
    if (setRecommendationFlag) {
      setRecommendationFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, macroplanogramList, store, timeStamp, zoneId, includeDraft, urlParams.demo]);

  return (
    <div>
      <Row alignmentVertical="bottom">
        <StoreSelector selectedStoreId={store} onStoreSelect={setStore} />
        <CustomDatePicker value={date} onChange={setDate} label="Date" />
        {enableZoneListInHeader && (
          <Select
            label="UPHL2"
            value={zoneId}
            onChange={handleZoneChange}
            placeholder="Select Zone..."
            width={300}
            error={zoneSelectionFlag}
          >
            {filteredZoneList &&
              store &&
              timeStamp &&
              filteredZoneList.map((zoneName) => <SelectOption value={zoneName.zoneId} label={zoneName.zoneName} />)}
            <SelectOption value="" label="None" />
          </Select>
        )}
        {zoneSelectionFlag && enableZoneListInHeader && (
          <Alert size="small" type="error">
            Please select a zone
          </Alert>
        )}
        {draftChanges && (
          <Toggle size="large" checked={includeDraft} onChange={setIncludeDraft}>
            <Text type="b400">Draft Assortment Only</Text>
          </Toggle>
        )}
      </Row>
    </div>
  );
};
export default AtAgileHeader;
